import {
  BatchClientInsurerType,
  ContractType,
  PartnershipType,
} from '../../types';
import {
  ActiveStatus,
  computeActiveStatus,
  extractProductPackages,
  findActiveContract,
  getMostExpensiveBillingModelName,
} from '../../utils/contract-utils';
import { computeEffectiveRange } from '../../utils/date-helper';
import {
  CiStatusFilter,
  ClientInsurerIndexFilterModel,
  ContractStatusFilter,
} from './client-insurer-index-types';

export interface ClientInsurerIndexRow {
  id: number;
  clientId: number;
  clientName: string;
  insurerName: string;
  contractId: number | null;
  contractPartnershipId: number | null;
  contractPartnershipName: string | null;
  apPartnershipName: string | null;
  effectiveDate: string | null;
  contractType: string | null;
  packageDetails: {
    contractPackages: {
      key: string;
      contractPackage: ContractType['contractPackages'][number];
    }[];
    chronicPrice: number | null;
    acutePrice: number | null;
    inPersonVisitPrice: number | null;
    currency: string;
    packageEnabled: boolean;
  } | null;
  contractStatus: ActiveStatus | null;
  contractDateRange: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  billingType: string | null;
  apBillingType: string | null;
  coreContractRule: string | null;
}

/**
 * Builds skeleton rows from CI data only. Contract-dependent fields are null.
 */
export const generateSkeletonRows = (
  clientInsurers: BatchClientInsurerType[],
): ClientInsurerIndexRow[] =>
  clientInsurers.map(ci => {
    const apPartnership = ci.partnerships.find(
      p => p.partnershipType === 'billing',
    );

    return {
      id: ci.id,
      clientId: ci.client.id,
      clientName: ci.client.name,
      insurerName: ci.insurer.name,
      contractId: null,
      contractPartnershipId: null,
      contractPartnershipName: null,
      apPartnershipName: apPartnership?.name ?? null,
      effectiveDate: ci.effectiveDate ?? null,
      contractType: null,
      packageDetails: null,
      contractStatus: null,
      contractDateRange: null,
      contractStartDate: null,
      contractEndDate: null,
      billingType: null,
      apBillingType: ci.billingType ?? null,
      coreContractRule: ci.coreContractRule ?? null,
    };
  });

/**
 * Enriches existing rows with contract data for the given CI IDs.
 * Only rows with active contracts are updated
 */
export const enrichRowsWithContracts = (
  rows: ClientInsurerIndexRow[],
  contracts: ContractType[],
  partnerships: PartnershipType[],
): ClientInsurerIndexRow[] => {
  const contractsByCiId = new Map<number, ContractType[]>();
  for (const contract of contracts) {
    const existing = contractsByCiId.get(contract.clientsInsurerId) ?? [];
    existing.push(contract);
    contractsByCiId.set(contract.clientsInsurerId, existing);
  }

  return rows.map(row => {
    const ciContracts = contractsByCiId.get(row.id);
    const activeContract = findActiveContract(ciContracts ?? []);
    if (!activeContract) return row;

    return {
      ...row,
      contractId: activeContract.id,
      contractPartnershipId: activeContract.partnershipId ?? null,
      contractPartnershipName: activeContract.partnershipId
        ? (partnerships.find(p => p.id === activeContract.partnershipId)
            ?.name ?? null)
        : null,
      contractType:
        activeContract.contractType ??
        getMostExpensiveBillingModelName(activeContract.contractPackages) ??
        null,
      packageDetails: {
        contractPackages: extractProductPackages(
          activeContract.contractPackages,
        ),
        chronicPrice: activeContract.chronicPrice ?? null,
        acutePrice: activeContract.acutePrice ?? null,
        inPersonVisitPrice: activeContract.contractPackages[0]?.price ?? null,
        currency: activeContract.currency ?? 'USD',
        packageEnabled: activeContract.packageEnabled,
      },
      contractStatus: computeActiveStatus(
        activeContract.void,
        activeContract.startDate,
        activeContract.endDate,
      ),
      contractDateRange: computeEffectiveRange(
        activeContract.startDate,
        activeContract.endDate,
      ),
      contractStartDate: activeContract.startDate ?? null,
      contractEndDate: activeContract.endDate ?? null,
      billingType: activeContract.billingType ?? null,
    };
  });
};

export const filterByIsActive = (
  cis: BatchClientInsurerType[],
  ciStatus: CiStatusFilter,
): BatchClientInsurerType[] => {
  if (ciStatus === CiStatusFilter.Active) {
    return cis.filter(ci => ci.isActive);
  }
  if (ciStatus === CiStatusFilter.Inactive) {
    return cis.filter(ci => !ci.isActive);
  }
  return cis;
};

export const hasContractFilters = (
  params: ClientInsurerIndexFilterModel,
): boolean =>
  params.partnershipIds.length > 0 ||
  params.billingTypes.length > 0 ||
  params.contractStatus !== ContractStatusFilter.All;

export const paginateItems = <T>(
  items: T[],
  page: number,
  pageSize: number,
): T[] => items.slice(page * pageSize, page * pageSize + pageSize);

export const allHaveActiveContracts = (
  rows: ClientInsurerIndexRow[],
): boolean => rows.every(r => r.contractStatus === ActiveStatus.Active);

export const applyContractFilters = (
  rows: ClientInsurerIndexRow[],
  params: ClientInsurerIndexFilterModel,
): ClientInsurerIndexRow[] => {
  let filtered = rows;

  if (params.partnershipIds.length > 0) {
    const ids = new Set(params.partnershipIds);
    filtered = filtered.filter(
      r => r.contractPartnershipId !== null && ids.has(r.contractPartnershipId),
    );
  }

  if (params.billingTypes.length > 0) {
    const types = new Set(params.billingTypes);
    filtered = filtered.filter(
      r => r.billingType !== null && types.has(r.billingType),
    );
  }

  if (params.contractStatus === ContractStatusFilter.Active) {
    filtered = filtered.filter(r => r.contractStatus === ActiveStatus.Active);
  } else if (params.contractStatus === ContractStatusFilter.NotActive) {
    filtered = filtered.filter(r => r.contractStatus !== ActiveStatus.Active);
  }

  return filtered;
};
